import {produce} from 'immer';
import {assign, setup, fromPromise, assertEvent} from 'xstate';

import {AntibodyFormat} from 'antibodies/AntibodyTypedefs';
import {formatHasBothDomainTypes, isVariableDomain} from 'antibodies/AntibodyUtils';
import {ProteinCreationSettingsData} from 'antibodies/nested-data-creation/full-screen/ProteinCreationSettingsUtil';
import {
  getSubmissionValidationErrors,
  guardRelatedSchemasConfigIsValid,
  guardSelectedProjectFolderIsValid,
  hasSubmissionValidationErrors,
} from 'antibodies/nested-data-creation/ProteinCreationSharedUtil';
import * as AntibodyNestedDataCreationSpreadsheetImportDAO from 'antibodies/nested-data-creation/spreadsheet-import/AntibodyNestedDataCreationSpreadsheetImportDAO';
import {
  InvalidProteinSchemaError,
  QueryAntibodyComponentAndRelatedSchemasInput,
  QueryProteinSchemaValidityInput,
} from 'antibodies/nested-data-creation/spreadsheet-import/AntibodyNestedDataCreationSpreadsheetImportDAO';
import {
  columnsIncludeDnaType,
  getColumnInferenceErrorMessages,
  getResolvedColumnType,
} from 'antibodies/nested-data-creation/spreadsheet-import/AntibodyNestedDataCreationSpreadsheetImportUtil';
import {
  COMPONENT_AND_RELATED_SCHEMAS_FETCH_ERROR_MESSAGE,
  INVALID_PROTEIN_SCHEMA_ERROR_MESSAGE,
  TranslationParameters,
} from 'antibodies/nested-data-creation/spreadsheet-import/constants/AntibodyNestedDataCreationSpreadsheetImportConstants';
import {
  AntibodyNestedDataCreationSpreadsheetImportState,
  AntibodyComponentAndRelatedSchemas,
  INITIAL_ANTIBODY_NESTED_DATA_CREATION_SPREADSHEET_IMPORT_STATE,
  INITIAL_GERMLINE_CONFIG,
  NdcColumnState,
  NdcColumnInferenceResult,
  NdcSchemaFieldColumnState,
  NdcSpreadsheetComponentFormatType,
  NdcGermlineConfig,
  NdcGermlineConfigRequest,
  NdcRelatedSchemasConfig,
  isNdcColumnInferenceResult,
  isNdcColumnInferenceResponse,
  isNdcSchemaFieldColumnInferenceResult,
  NdcColumnInferenceRequest,
  NdcColumnInferenceResponse,
  NdcSpreadsheetImportColumnType,
  NdcSpreadsheetImportSchemaFieldColumnType,
  NdcSpreadsheetImportRequest,
  NdcSpreadsheetImportSubmissionValidationErrors,
  NdcSpreadsheetInterpretation,
} from 'antibodies/nested-data-creation/spreadsheet-import/constants/AntibodyNestedDataCreationSpreadsheetImportConstants';
import {assert, assertNotNullAndReturn} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {AntibodyDomainType, RegistryLabelingStrategy} from 'graphql-client/__generated/graphqlGlobalTypes';
import navigateToURL from 'lib/navigateToURL';
import notifications from 'notifications';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import * as logging from 'util/Logging';
import {getUserMessage, isServerRequestError} from 'util/Net';

async function handleInferColumnsForNestedCreateAndRegisterSpreadsheet(
  input: NdcColumnInferenceRequest
): Promise<NdcColumnInferenceResponse | {errorMessage: string}> {
  try {
    const result =
      await AntibodyNestedDataCreationSpreadsheetImportDAO.inferColumnsForNestedCreateAndRegisterSpreadsheet(
        input
      );
    return result;
  } catch (e) {
    if (isServerRequestError(e)) {
      const userMessage = getUserMessage(e);
      const errorMessage = userMessage ?? 'Failed to infer column types for spreadsheet import';
      return {errorMessage};
    } else {
      logging.error(e, {extra: {message: 'Failed to infer column types for spreadsheet import'}});
      notifications.notifyError(e);
      throw e;
    }
  }
}

async function handleNestedCreateAndRegisterFromSpreadsheetImport(
  input: NdcSpreadsheetImportRequest
): Promise<void | {submissionValidationErrors: NdcSpreadsheetImportSubmissionValidationErrors}> {
  try {
    LoadingScreen.show('Creating proteins from spreadsheet...');
    const result =
      await AntibodyNestedDataCreationSpreadsheetImportDAO.nestedCreateAndRegisterFromSpreadsheetImport(
        input
      );
    if (result.ndcWorkspaceUrl) {
      notifications.info('Open the flowchart to view progress.', {
        actionText: 'View flowchart',
        onActionClick: () => {
          navigateToURL(result.ndcWorkspaceUrl!);
        },
      });
    } else {
      logging.error('No NDC workspace URL returned from nested create and register from spreadsheet import');
      notifications.info("We're working on creating proteins and components...");
    }
  } catch (e) {
    if (e.status === 400) {
      const submissionValidationErrors = getSubmissionValidationErrors(e);
      if (submissionValidationErrors != null) {
        return {submissionValidationErrors};
      }
    }
    logging.error(e, {
      extra: {message: 'Failed to create and register proteins from spreadsheet import'},
    });
    notifications.notifyError(e);
    throw e;
  } finally {
    LoadingScreen.hide();
  }
}

function guardAntibodySchemaAndFormatAndRegistryAreSet(
  context: AntibodyNestedDataCreationSpreadsheetImportState
): boolean {
  return (
    context.selectedFormat?.id != null &&
    context.selectedSchemaId != null &&
    context.selectedRegistryId != null
  );
}

function guardRequiredGermlinesAreSelected(
  context: AntibodyNestedDataCreationSpreadsheetImportState
): boolean {
  const {germlineConfig} = context;
  if (germlineConfig.useMultipleGermlines) {
    return (
      germlineConfig.selectedVariableGermlineId != null && germlineConfig.selectedConstantGermlineId != null
    );
  }
  return germlineConfig.selectedGermlineId != null;
}

/**
 * Apply downstream effects to context when the format changes.
 *
 * Automatically changing starting type from VARIABLE_PAIRS to DOMAINS if the selected format has no variable pairs.
 */
function applyFormatSideEffects(
  draft: AntibodyNestedDataCreationSpreadsheetImportState,
  format: AntibodyFormat | null
): void {
  if (
    format != null &&
    format.distinctVariablePairs.length === 0 &&
    draft.selectedStartingType === NdcSpreadsheetComponentFormatType.VARIABLE_PAIRS
  ) {
    draft.selectedStartingType = NdcSpreadsheetComponentFormatType.DOMAINS;
  }
  // Reset germline config if the new format doesn't support multiple germlines
  // but multiple germlines was previously selected.
  if (format != null && !formatHasBothDomainTypes(format) && draft.germlineConfig.useMultipleGermlines) {
    draft.germlineConfig = INITIAL_GERMLINE_CONFIG;
  }
}

function guardSpreadsheetSetAndInterpretedWithoutError(
  context: AntibodyNestedDataCreationSpreadsheetImportState
): boolean {
  return (
    context.spreadsheetFile != null &&
    context.spreadsheetURL != null &&
    context.spreadsheetInterpretation != null &&
    context.columnInferenceErrorMessages.length === 0
  );
}

function guardSpreadsheetSetAndNotInferred(
  context: AntibodyNestedDataCreationSpreadsheetImportState
): boolean {
  return (
    context.spreadsheetFile != null &&
    context.spreadsheetURL != null &&
    context.spreadsheetInterpretation == null &&
    context.columnInferenceErrorMessages.length === 0
  );
}

/** Guards that column inference returned a valid response. Structural columns must have non-empty inferred
 * types; schema-field columns are always valid (their target field determines parsing). Returns false for
 * spreadsheet-wide errors, per-column errors, or structural columns with no inferred types. */
function guardColumnInferenceSucceededAndIsValid(
  output: NdcColumnInferenceResponse | {errorMessage: string}
): boolean {
  return (
    isNdcColumnInferenceResponse(output) &&
    output.columnTypes.every((columnType) => {
      if (isNdcSchemaFieldColumnInferenceResult(columnType)) {
        return true;
      }
      if (isNdcColumnInferenceResult(columnType) && columnType.inferredComponentTypes.length > 0) {
        return true;
      }
      if (isNdcColumnInferenceResult(columnType) && columnType.inferredComponentTypes.length === 0) {
        logging.error('Received column type with no inferred types or error message');
      }
      return false;
    })
  );
}

function parseColumnInferenceResponseToSpreadsheetInterpretation(
  columnInferenceResponse: NdcColumnInferenceResponse
): NdcSpreadsheetInterpretation {
  const columns: Array<NdcColumnState> = [];
  const schemaFieldColumns: Array<NdcSchemaFieldColumnState> = [];

  columnInferenceResponse.columnTypes.forEach((column: NdcColumnInferenceResult) => {
    if (isNdcSchemaFieldColumnInferenceResult(column)) {
      schemaFieldColumns.push({
        columnName: column.columnName,
        sampleValue: column.sampleValue,
        componentFormatKey: column.componentFormatKey,
        fieldType: column.fieldType,
        isRequired: column.isRequired,
      });
      return;
    }
    assert(isNdcColumnInferenceResult(column), 'Expected all remaining columns to be valid inferred columns');
    columns.push({
      columnName: column.columnName,
      sampleValue: column.sampleValue,
      componentFormatKey: column.componentFormatKey,
      // Asserted to not be empty in guardColumnInferenceSucceededAndIsValid
      inferredTypes: column.inferredComponentTypes,
      // We automatically set the selected type to the only inferred type if there is only
      // one, so that the ColumnMappingPane knows not to display a warning to the user for
      // this column.
      selectedType: column.inferredComponentTypes.length === 1 ? column.inferredComponentTypes[0] : null,
    });
  });

  return {
    columns,
    schemaFieldColumns,
    rowCount: columnInferenceResponse.rowCount,
  };
}

function guardSpreadsheetInterpretationIsValid(
  spreadsheetInterpretation: NdcSpreadsheetInterpretation | null
): boolean {
  return (
    spreadsheetInterpretation != null &&
    spreadsheetInterpretation.columns.every((column) => column.inferredTypes.length > 0)
  );
}

/** Validates that all related schemas are available, returning an error message if any of the user-selectable
 * related schemas are missing. DNA schemas are only validated when the columns include DNA types. */
function validateAvailableSchemas(
  schemas: AntibodyComponentAndRelatedSchemas,
  columns: Array<NdcColumnState>
): string | null {
  const missingSchemas = [];
  if (columnsIncludeDnaType(columns)) {
    if (schemas.dnaDomainSchemaIds.size === 0) {
      missingSchemas.push('DNA domain');
    }
    if (schemas.dnaChainSchemaIds.size === 0) {
      missingSchemas.push('DNA chain');
    }
    if (schemas.dnaPairSchemaIds.size === 0) {
      missingSchemas.push('DNA pair');
    }
  }
  if (schemas.aaPairSchemaIds.size === 0) {
    missingSchemas.push('AA pair');
  }
  return missingSchemas.length > 0 ? `No ${missingSchemas.join(', ')} schemas found in registry` : null;
}

function getComponentAndRelatedSchemasQueryInput(
  context: AntibodyNestedDataCreationSpreadsheetImportState
): QueryAntibodyComponentAndRelatedSchemasInput {
  assert(context.selectedRegistryId != null, 'Registry ID should be set');
  assert(context.selectedSchemaId != null, 'Schema ID should be set');
  return {
    registryId: context.selectedRegistryId,
    proteinSchemaId: context.selectedSchemaId,
  };
}

function getProteinSchemaValidityQueryInput(
  context: AntibodyNestedDataCreationSpreadsheetImportState
): QueryProteinSchemaValidityInput {
  assert(context.selectedRegistryId != null, 'Registry ID should be set');
  assert(context.selectedSchemaId != null, 'Schema ID should be set');
  return {
    registryId: context.selectedRegistryId,
    proteinSchemaId: context.selectedSchemaId,
  };
}

/** Returns the germline ID for a given domain type based on the germline config.
 *
 * Linker, fusion, and element domains are excluded — they don't use germlines.
 * In single germline mode, all other domains get the same germline ID.
 * In multiple germlines mode, variable domains get the variable germline and everything else
 * gets the constant germline (including hinge and J-chain, which PipeBio treats as constant). */
function getGermlineIdForDomain(
  germlineConfig: NdcGermlineConfig,
  domainType: AntibodyDomainType
): string | null {
  if (
    domainType === AntibodyDomainType.L ||
    domainType === AntibodyDomainType.F ||
    domainType === AntibodyDomainType.ELEMENT
  ) {
    return null;
  }
  if (!germlineConfig.useMultipleGermlines) {
    return germlineConfig.selectedGermlineId;
  }
  if (isVariableDomain(domainType)) {
    return germlineConfig.selectedVariableGermlineId;
  }
  return germlineConfig.selectedConstantGermlineId;
}

/** Builds the germlineConfigs array mapping each distinct domain key to the appropriate germline ID.
 * Returns null when no germline IDs are resolved (e.g. when germline selectors are not shown). */
function buildGermlineConfigs(
  germlineConfig: NdcGermlineConfig,
  format: AntibodyFormat | null
): Array<NdcGermlineConfigRequest> | null {
  assert(format != null, 'Format should be set before building germline configs');

  const configs: Array<NdcGermlineConfigRequest> = [];
  for (const domain of format.domains) {
    const germlineId = getGermlineIdForDomain(germlineConfig, domain.type);
    if (germlineId != null) {
      configs.push({domainKey: {label: domain.label}, germlineId});
    }
  }
  return configs.length > 0 ? configs : null;
}

/** Prepares the NdcSpreadsheetImportRequest for submitting the spreadsheet. Will assert on all required
 * fields being ready, assuming that all of these have been guarded against in earlier state transitions. */
function prepareNdcSpreadsheetImportRequest(
  context: AntibodyNestedDataCreationSpreadsheetImportState
): NdcSpreadsheetImportRequest {
  assert(context.selectedProjectFolderId != null, 'Project folder ID should be set');
  assert(context.selectedSchemaId != null, 'Schema ID should be set');
  assert(context.spreadsheetURL != null, 'Spreadsheet URL should be set');
  assert(context.spreadsheetInterpretation != null, 'Spreadsheet interpretation should be set');
  assert(
    guardRelatedSchemasConfigIsValid(
      context.relatedSchemasConfig,
      columnsIncludeDnaType(context.spreadsheetInterpretation.columns)
    ),
    'Related schemas config should be valid'
  );

  const structuralColumns: Array<NdcSpreadsheetImportColumnType> =
    context.spreadsheetInterpretation.columns.map((column) => {
      const resolvedType = getResolvedColumnType(column);

      return {
        columnName: column.columnName,
        componentFormatKey: column.componentFormatKey,
        ...resolvedType,
      };
    });

  // Schema-field columns carry no component type; their componentFormatKey (with fieldId) is echoed back
  // so the create request is self-describing.
  const schemaFieldColumns: Array<NdcSpreadsheetImportSchemaFieldColumnType> = (
    context.spreadsheetInterpretation.schemaFieldColumns ?? []
  ).map((column) => ({
    columnName: column.columnName,
    componentFormatKey: column.componentFormatKey,
  }));

  const columns: Array<NdcSpreadsheetImportColumnType | NdcSpreadsheetImportSchemaFieldColumnType> = [
    ...structuralColumns,
    ...schemaFieldColumns,
  ];

  return {
    folderId: context.selectedProjectFolderId,
    proteinSchemaId: context.selectedSchemaId,
    spreadsheetLocation: context.spreadsheetURL,
    columns,
    translationParameters: context.translationParameters,
    documentId: context.selectedDocumentId,
    germlineConfigs: buildGermlineConfigs(context.germlineConfig, context.selectedFormat),
    newComponentConfig: {
      aaPairSchemaId: context.relatedSchemasConfig.aaPairSchemaId,
      dnaDomainSchemaId: context.relatedSchemasConfig.dnaDomainSchemaId,
      dnaChainSchemaId: context.relatedSchemasConfig.dnaChainSchemaId,
      dnaPairSchemaId: context.relatedSchemasConfig.dnaPairSchemaId,
    },
  };
}

type AntibodyNestedDataCreationSpreadsheetImportModalEvents =
  // Navigation
  | {type: 'next'}
  | {type: 'back'}
  // Select Format pane
  | {type: 'selectFormat'; format: AntibodyFormat | null}
  | {
      type: 'selectSchema';
      schemaId: string | null;
      schemaName: string | null;
      registryId: string | null;
      format: AntibodyFormat | null;
    }
  | {type: 'setStartingType'; startingType: NdcSpreadsheetComponentFormatType}
  | {type: 'setGermlineConfig'; germlineConfig: NdcGermlineConfig}
  | {type: 'setNamingStrategy'; namingStrategy: RegistryLabelingStrategy}
  // Upload Spreadsheet pane
  | {type: 'setSpreadsheetFile'; spreadsheetFile: File}
  | {type: 'setSpreadsheetURL'; spreadsheetURL: string}
  | {type: 'clearSpreadsheet'}
  // Column Mapping pane
  | {type: 'updateSpreadsheetInterpretation'; spreadsheetInterpretation: NdcSpreadsheetInterpretation}
  // Finalize pane
  | {type: 'setTranslationParameters'; translationParameters: TranslationParameters}
  | {type: 'setProjectFolder'; projectFolderId: string}
  | {type: 'setRelatedSchemasConfig'; relatedSchemasConfig: NdcRelatedSchemasConfig}
  | {type: 'setSelectedDocumentId'; documentId: string | null}
  | {type: 'submitSpreadsheetImport'}
  // Validation Errors pane
  | {type: 'backToUploadSpreadsheet'};

/**
 * State machine handling the progress and state of importing antibodies from a spreadsheet in a modal.
 *
 * -----------------------------------------------------------------------------------------------------------
 * | State                | Purpose                                                                          |
 * |----------------------|----------------------------------------------------------------------------------|
 * | selectFormat         | User picks antibody format, schema, registry, starting type (domains/chains/pairs)|
 * | uploadSpreadsheet    | User uploads a spreadsheet file (stored locally + S3 URL), backend infers column |
 * |                      | types from headers and first row and displays errors requiring reupload          |
 * | columnMapping        | User reviews/picks inferred column types for each spreadsheet column when        |
 * |                      | multiple inferred types are present                                              |
 * | finalize             | User picks project folder and related schemas                                    |
 * | saving               | Loading state while backend validates and kicks off the pipeline                 |
 * | validationErrors     | Shows validation errors thrown during validation (e.g. missing entities)         |
 * | done                 | Modal closes since pipeline was successfully kicked off                          |
 * ---------------------------------------------------------------------------------------------------------*/
const AntibodyNestedDataCreationSpreadsheetImportModalMachine = setup({
  types: {
    context: {} as AntibodyNestedDataCreationSpreadsheetImportState,
    events: {} as AntibodyNestedDataCreationSpreadsheetImportModalEvents,
    input: {} as {
      initialProjectFolderId?: string | null;
      /** Settings from an in-progress protein creation flow, used to pre-fill the select format step. */
      initialSettings?: ProteinCreationSettingsData;
    },
  },
  actors: {
    inferColumnTypes: fromPromise(
      async ({
        input,
      }: {
        input: NdcColumnInferenceRequest;
      }): Promise<NdcColumnInferenceResponse | {errorMessage: string}> =>
        await handleInferColumnsForNestedCreateAndRegisterSpreadsheet(input)
    ),
    submitSpreadsheetImport: fromPromise(
      async ({input}: {input: NdcSpreadsheetImportRequest}) =>
        await handleNestedCreateAndRegisterFromSpreadsheetImport(input)
    ),
    queryComponentAndRelatedSchemas: fromPromise(
      async ({
        input,
      }: {
        input: QueryAntibodyComponentAndRelatedSchemasInput;
      }): Promise<AntibodyComponentAndRelatedSchemas> =>
        await AntibodyNestedDataCreationSpreadsheetImportDAO.queryAntibodyComponentAndRelatedSchemas(input)
    ),
    validateProteinSchema: fromPromise(
      async ({input}: {input: QueryProteinSchemaValidityInput}): Promise<void> => {
        await AntibodyNestedDataCreationSpreadsheetImportDAO.queryProteinSchemaValidity(input);
      }
    ),
  },
  actions: {
    // Select Format pane
    selectFormat: assign(({context, event}) => {
      assertEvent(event, 'selectFormat');
      const newFormatId = event.format?.id ?? null;

      return produce(context, (draft) => {
        draft.selectedFormat = event.format;

        // Clear schema and registry if format changed
        if (context.selectedFormat?.id !== newFormatId) {
          draft.selectedSchemaId = null;
          draft.selectedSchemaName = null;
          draft.selectedRegistryId = null;
          draft.isSelectedSchemaValid = false;
          draft.selectedSchemaValidationErrorMessage = null;
          draft.namingStrategy = null;
        }
        applyFormatSideEffects(draft, event.format);
      });
    }),
    selectSchema: assign(({context, event}) => {
      assertEvent(event, 'selectSchema');
      // A schema ID maps to one parent registry ID. We still compare both values here because
      // selectedRegistryId in state can be stale; if a re-selection emits the same schemaId with
      // a corrected registryId, we must refresh downstream state.
      if (context.selectedSchemaId === event.schemaId && context.selectedRegistryId === event.registryId) {
        return context;
      }
      // Reset downstream state that depends on the schema. Spreadsheet file/URL are preserved so
      // the user doesn't have to re-upload; re-inference triggers automatically on navigation.
      const baseState = {
        ...INITIAL_ANTIBODY_NESTED_DATA_CREATION_SPREADSHEET_IMPORT_STATE,
        selectedFormat: event.format ?? null,
        selectedRegistryId: event.registryId,
        selectedStartingType: context.selectedStartingType,
        selectedProjectFolderId: context.selectedProjectFolderId,
        translationParameters: context.translationParameters,
        spreadsheetFile: context.spreadsheetFile,
        spreadsheetURL: context.spreadsheetURL,
        selectedSchemaId: event.schemaId,
        selectedSchemaName: event.schemaName,
        germlineConfig: context.germlineConfig,
      };

      return produce(baseState, (draft) => {
        applyFormatSideEffects(draft, event.format);
      });
    }),
    setStartingType: assign(({context, event}) => {
      assertEvent(event, 'setStartingType');
      if (context.selectedStartingType === event.startingType) {
        return context;
      }
      // Reset downstream state that depends on starting type. Spreadsheet file/URL are preserved so
      // the user doesn't have to re-upload; re-inference triggers automatically on navigation.
      return {
        ...INITIAL_ANTIBODY_NESTED_DATA_CREATION_SPREADSHEET_IMPORT_STATE,
        selectedFormat: context.selectedFormat,
        selectedSchemaId: context.selectedSchemaId,
        selectedSchemaName: context.selectedSchemaName,
        selectedRegistryId: context.selectedRegistryId,
        // Starting type doesn't affect schema validity, so preserve it rather than forcing re-validation.
        isSelectedSchemaValid: context.isSelectedSchemaValid,
        selectedSchemaValidationErrorMessage: context.selectedSchemaValidationErrorMessage,
        selectedProjectFolderId: context.selectedProjectFolderId,
        translationParameters: context.translationParameters,
        spreadsheetFile: context.spreadsheetFile,
        spreadsheetURL: context.spreadsheetURL,
        selectedStartingType: event.startingType,
        germlineConfig: context.germlineConfig,
        namingStrategy: context.namingStrategy,
      };
    }),
    setGermlineConfig: assign(({context, event}) => {
      assertEvent(event, 'setGermlineConfig');
      return produce(context, (draft) => {
        draft.germlineConfig = event.germlineConfig;
      });
    }),
    setNamingStrategy: assign(({context, event}) => {
      assertEvent(event, 'setNamingStrategy');
      return produce(context, (draft) => {
        draft.namingStrategy = event.namingStrategy;
      });
    }),
    prefetchComponentAndRelatedSchemas: ({context}) => {
      const queryInput = getComponentAndRelatedSchemasQueryInput(context);
      // Warm Apollo cache in the background while users are on earlier panes.
      // Finalize still performs its own query and owns all loading/error state.
      AntibodyNestedDataCreationSpreadsheetImportDAO.queryAntibodyComponentAndRelatedSchemas(
        queryInput
      ).catch(() => undefined);
    },
    // Upload Spreadsheet pane
    setSpreadsheetFile: assign(({context, event}) => {
      assertEvent(event, 'setSpreadsheetFile');
      return produce(context, (draft) => {
        draft.spreadsheetFile = event.spreadsheetFile;
        draft.spreadsheetInterpretation = null;
        draft.columnInferenceErrorMessages = [];
      });
    }),
    setSpreadsheetURL: assign(({context, event}) => {
      assertEvent(event, 'setSpreadsheetURL');
      return produce(context, (draft) => {
        draft.spreadsheetURL = event.spreadsheetURL;
        draft.spreadsheetInterpretation = null;
        draft.columnInferenceErrorMessages = [];
      });
    }),
    clearSpreadsheet: assign(({context, event}) => {
      assertEvent(event, 'clearSpreadsheet');
      return produce(context, (draft) => {
        draft.spreadsheetFile = null;
        draft.spreadsheetURL = null;
        draft.spreadsheetInterpretation = null;
        draft.columnInferenceErrorMessages = [];
      });
    }),
    // Column Mapping pane
    updateSpreadsheetInterpretation: assign(({context, event}) => {
      assertEvent(event, 'updateSpreadsheetInterpretation');
      return produce(context, (draft) => {
        draft.spreadsheetInterpretation = event.spreadsheetInterpretation;
      });
    }),
    // Finalize pane
    setTranslationParameters: assign(({context, event}) => {
      assertEvent(event, 'setTranslationParameters');
      return produce(context, (draft) => {
        draft.translationParameters = event.translationParameters;
      });
    }),
    setProjectFolder: assign(({context, event}) => {
      assertEvent(event, 'setProjectFolder');
      return produce(context, (draft) => {
        draft.selectedProjectFolderId = event.projectFolderId;
      });
    }),
    setRelatedSchemasConfig: assign(({context, event}) => {
      assertEvent(event, 'setRelatedSchemasConfig');
      return produce(context, (draft) => {
        draft.relatedSchemasConfig = event.relatedSchemasConfig;
      });
    }),
    resetStateForSpreadsheetReupload: assign(({context, event}) => {
      assertEvent(event, 'backToUploadSpreadsheet');
      return {
        ...INITIAL_ANTIBODY_NESTED_DATA_CREATION_SPREADSHEET_IMPORT_STATE,
        selectedFormat: context.selectedFormat,
        selectedSchemaId: context.selectedSchemaId,
        selectedSchemaName: context.selectedSchemaName,
        selectedRegistryId: context.selectedRegistryId,
        // The schema was already validated to reach this point, so preserve its validity.
        isSelectedSchemaValid: context.isSelectedSchemaValid,
        selectedSchemaValidationErrorMessage: context.selectedSchemaValidationErrorMessage,
        selectedStartingType: context.selectedStartingType,
        selectedProjectFolderId: context.selectedProjectFolderId,
        translationParameters: context.translationParameters,
        germlineConfig: context.germlineConfig,
        namingStrategy: context.namingStrategy,
      };
    }),
    setSelectedDocumentId: assign(({context, event}) => {
      assertEvent(event, 'setSelectedDocumentId');
      return produce(context, (draft) => {
        draft.selectedDocumentId = event.documentId;
      });
    }),
  },
}).createMachine({
  context: ({input}) =>
    produce(INITIAL_ANTIBODY_NESTED_DATA_CREATION_SPREADSHEET_IMPORT_STATE, (draft) => {
      draft.selectedProjectFolderId =
        input.initialProjectFolderId ??
        FolderDefaults.get(FolderDefaultsKey.ANTIBODY) ??
        FolderDefaults.get(FolderDefaultsKey.INVENTORY) ??
        null;
      const {initialSettings} = input;
      if (initialSettings != null) {
        draft.selectedFormat = initialSettings.selectedFormat;
        draft.selectedSchemaId = initialSettings.selectedSchemaId;
        draft.selectedSchemaName = initialSettings.selectedSchemaName;
        draft.selectedRegistryId = initialSettings.selectedRegistryId;
        draft.selectedStartingType = initialSettings.selectedStartingType;
        draft.germlineConfig = initialSettings.germlineConfig;
        draft.namingStrategy = initialSettings.namingStrategy;
        // The schema was validated when these settings were first selected.
        draft.isSelectedSchemaValid = true;
      }
    }),
  id: 'antibodyNestedDataCreationSpreadsheetImportModal',
  initial: 'selectFormat',
  states: {
    selectFormat: {
      initial: 'idle',
      on: {
        next: [
          {
            target: 'redirectToFullScreen',
            guard: ({context}) =>
              BenchlingConfig.REGISTRY_ENABLE_FULL_SCREEN_PROTEIN_REGISTRATION &&
              guardAntibodySchemaAndFormatAndRegistryAreSet(context) &&
              guardRequiredGermlinesAreSelected(context) &&
              context.isSelectedSchemaValid,
          },
          {
            target: 'uploadSpreadsheet',
            guard: ({context}) =>
              guardAntibodySchemaAndFormatAndRegistryAreSet(context) &&
              guardRequiredGermlinesAreSelected(context) &&
              context.isSelectedSchemaValid,
            actions: 'prefetchComponentAndRelatedSchemas',
          },
        ],
        selectFormat: {actions: 'selectFormat'},
        // Validate the schema up front so we can block advancing before the user completes the flow.
        selectSchema: [
          {
            target: '.validatingSchema',
            reenter: true,
            // Only validate a real selection; a cleared one falls through to idle and cannot advance.
            guard: ({event}) => {
              assertEvent(event, 'selectSchema');
              return event.schemaId != null && event.registryId != null;
            },
            actions: 'selectSchema',
          },
          {
            target: '.idle',
            actions: 'selectSchema',
          },
        ],
        setStartingType: {actions: 'setStartingType'},
        setGermlineConfig: {actions: 'setGermlineConfig'},
        setNamingStrategy: {actions: 'setNamingStrategy'},
      },
      states: {
        idle: {},
        validatingSchema: {
          invoke: {
            src: 'validateProteinSchema',
            input: ({
              context,
            }: {
              context: AntibodyNestedDataCreationSpreadsheetImportState;
            }): QueryProteinSchemaValidityInput => getProteinSchemaValidityQueryInput(context),
            onDone: {
              target: 'idle',
              actions: assign(({context}) =>
                produce(context, (draft) => {
                  draft.isSelectedSchemaValid = true;
                  draft.selectedSchemaValidationErrorMessage = null;
                })
              ),
            },
            onError: {
              target: 'idle',
              actions: assign(({context, event}) => {
                const error = event.error;
                const isInvalidSchema = error instanceof InvalidProteinSchemaError;
                // Logged at warning level: this is user-recoverable and not fatal, but a selected schema
                // failing validation is unexpected since invalid unarchived schemas should be rare.
                logging.warn(error as Error, {
                  extra: {
                    message: isInvalidSchema
                      ? 'Selected protein schema failed validation'
                      : 'Failed to validate selected protein schema',
                    selectedSchemaId: context.selectedSchemaId,
                    selectedSchemaName: context.selectedSchemaName,
                    selectedRegistryId: context.selectedRegistryId,
                    errorMessage: error instanceof Error ? error.message : String(error),
                  },
                });
                return produce(context, (draft) => {
                  draft.isSelectedSchemaValid = false;
                  draft.selectedSchemaValidationErrorMessage = isInvalidSchema
                    ? INVALID_PROTEIN_SCHEMA_ERROR_MESSAGE
                    : COMPONENT_AND_RELATED_SCHEMAS_FETCH_ERROR_MESSAGE;
                });
              }),
            },
          },
        },
      },
    },
    uploadSpreadsheet: {
      initial: 'selectingSpreadsheet',
      on: {
        next: {
          target: 'columnMapping',
          guard: ({context}) => guardSpreadsheetSetAndInterpretedWithoutError(context),
        },
        back: {
          target: 'selectFormat',
        },
      },
      states: {
        selectingSpreadsheet: {
          always: {
            target: 'inferringColumns',
            guard: ({context}) => guardSpreadsheetSetAndNotInferred(context),
          },
          on: {
            setSpreadsheetFile: {actions: 'setSpreadsheetFile'},
            setSpreadsheetURL: {actions: 'setSpreadsheetURL'},
            clearSpreadsheet: {actions: 'clearSpreadsheet'},
          },
        },
        inferringColumns: {
          invoke: {
            src: 'inferColumnTypes',
            input: ({
              context,
            }: {
              context: AntibodyNestedDataCreationSpreadsheetImportState;
            }): NdcColumnInferenceRequest => {
              assert(context.spreadsheetURL != null, 'Spreadsheet URL should be set');
              assert(context.selectedSchemaId != null, 'Schema ID should be set');
              return {
                spreadsheetLocation: context.spreadsheetURL,
                proteinSchemaId: context.selectedSchemaId,
                componentFormatType: context.selectedStartingType,
              };
            },
            onDone: [
              {
                target: 'columnsInferred',
                guard: ({event}) => guardColumnInferenceSucceededAndIsValid(event.output),
                actions: assign(({context, event}) => {
                  const columnInferenceResponse = event.output;
                  assert(
                    isNdcColumnInferenceResponse(columnInferenceResponse),
                    'Action guard should prevent action from running if column inference returned an error'
                  );
                  return produce(context, (draft) => {
                    draft.spreadsheetInterpretation =
                      parseColumnInferenceResponseToSpreadsheetInterpretation(columnInferenceResponse);
                  });
                }),
              },
              {
                target: 'showingError',
                guard: ({event}) => !guardColumnInferenceSucceededAndIsValid(event.output),
                actions: assign(({context, event}) => {
                  const errorMessages = getColumnInferenceErrorMessages(event.output);
                  assert(
                    errorMessages.length > 0,
                    'Column inference guard rejected the response but no error messages were extracted'
                  );
                  return produce(context, (draft) => {
                    draft.columnInferenceErrorMessages = errorMessages;
                  });
                }),
              },
            ],
            onError: {
              target: 'showingError',
              actions: assign(({context}) =>
                produce(context, (draft) => {
                  draft.columnInferenceErrorMessages = [
                    'An unexpected error occurred while processing your spreadsheet.',
                  ];
                })
              ),
            },
          },
          on: {
            clearSpreadsheet: {
              target: 'selectingSpreadsheet',
              actions: 'clearSpreadsheet',
            },
          },
        },
        columnsInferred: {
          on: {
            clearSpreadsheet: {
              target: 'selectingSpreadsheet',
              actions: 'clearSpreadsheet',
            },
          },
        },
        showingError: {
          on: {
            clearSpreadsheet: {
              target: 'selectingSpreadsheet',
              actions: 'clearSpreadsheet',
            },
          },
        },
      },
    },
    columnMapping: {
      on: {
        next: {
          target: 'finalize',
          guard: ({context}) => guardSpreadsheetInterpretationIsValid(context.spreadsheetInterpretation),
        },
        back: {
          target: 'uploadSpreadsheet',
        },
        updateSpreadsheetInterpretation: {
          actions: 'updateSpreadsheetInterpretation',
        },
      },
    },
    finalize: {
      invoke: {
        src: 'queryComponentAndRelatedSchemas',
        input: ({
          context,
        }: {
          context: AntibodyNestedDataCreationSpreadsheetImportState;
        }): QueryAntibodyComponentAndRelatedSchemasInput => getComponentAndRelatedSchemasQueryInput(context),
        onDone: {
          actions: assign(({context, event}) => {
            // spreadsheetInterpretation is guarded against being null in guardSpreadsheetInterpretationIsValid
            const componentAndRelatedSchemasErrorMessage = validateAvailableSchemas(
              event.output,
              assertNotNullAndReturn(context.spreadsheetInterpretation).columns
            );
            return produce(context, (draft) => {
              draft.componentAndRelatedSchemasErrorMessage = componentAndRelatedSchemasErrorMessage;
              draft.componentAndRelatedSchemas =
                componentAndRelatedSchemasErrorMessage == null ? event.output : null;
            });
          }),
        },
        onError: {
          actions: assign(({context, event}) => {
            const error = event.error;
            const isInvalidSchema = error instanceof InvalidProteinSchemaError;
            // Logged even though it is user-recoverable: a selected schema failing validation is unexpected.
            logging.error(error as Error, {
              extra: {
                message: isInvalidSchema
                  ? 'Selected protein schema failed validation'
                  : 'Failed to fetch component and related schemas',
                selectedSchemaId: context.selectedSchemaId,
                selectedSchemaName: context.selectedSchemaName,
                selectedRegistryId: context.selectedRegistryId,
                errorMessage: error instanceof Error ? error.message : String(error),
              },
            });

            return produce(context, (draft) => {
              // If finalize is revisited after a prior successful fetch, clear stale schema options on error.
              // The Finalize pane will show the error banner and hide selector contents until a successful refetch.
              draft.componentAndRelatedSchemas = null;
              draft.componentAndRelatedSchemasErrorMessage = isInvalidSchema
                ? INVALID_PROTEIN_SCHEMA_ERROR_MESSAGE
                : COMPONENT_AND_RELATED_SCHEMAS_FETCH_ERROR_MESSAGE;
            });
          }),
        },
      },
      on: {
        back: {
          target: 'columnMapping',
        },
        submitSpreadsheetImport: {
          target: 'saving',
          guard: ({context}) =>
            guardRelatedSchemasConfigIsValid(
              context.relatedSchemasConfig,
              columnsIncludeDnaType(assertNotNullAndReturn(context.spreadsheetInterpretation).columns)
            ) && guardSelectedProjectFolderIsValid(context.selectedProjectFolderId),
        },
        setTranslationParameters: {actions: 'setTranslationParameters'},
        setProjectFolder: {actions: 'setProjectFolder'},
        setRelatedSchemasConfig: {actions: 'setRelatedSchemasConfig'},
        setSelectedDocumentId: {actions: 'setSelectedDocumentId'},
      },
    },
    saving: {
      invoke: {
        src: 'submitSpreadsheetImport',
        input: ({
          context,
        }: {
          context: AntibodyNestedDataCreationSpreadsheetImportState;
        }): NdcSpreadsheetImportRequest => prepareNdcSpreadsheetImportRequest(context),
        onDone: [
          {
            target: 'validationErrors',
            guard: ({event}) => hasSubmissionValidationErrors(event.output?.submissionValidationErrors),
            actions: assign(({context, event}) =>
              produce(context, (draft) => {
                draft.submissionValidationErrors = event.output!.submissionValidationErrors;
              })
            ),
          },
          {
            target: 'done',
            guard: ({event}) => !hasSubmissionValidationErrors(event.output?.submissionValidationErrors),
            actions: ({context}) => {
              if (context.selectedProjectFolderId) {
                FolderDefaults.set(FolderDefaultsKey.ANTIBODY, context.selectedProjectFolderId);
              }
            },
          },
        ],
        onError: {
          target: 'finalize',
        },
      },
    },
    validationErrors: {
      on: {
        back: {
          target: 'finalize',
        },
        backToUploadSpreadsheet: {
          target: 'uploadSpreadsheet',
          actions: 'resetStateForSpreadsheetReupload',
        },
      },
    },
    redirectToFullScreen: {
      type: 'final',
    },
    done: {
      type: 'final',
    },
  },
});

export default AntibodyNestedDataCreationSpreadsheetImportModalMachine;
